import {
  Accordion,
  AccordionTypes,
  Button,
  ErrorBoundary,
  Icon,
  Shadow,
  Text,
  VStack,
} from '@genial/components';
import { BoxRadius, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import React, { memo, useCallback, useMemo, useState } from 'react';

import { MultipleAccountsSelectionProps } from '../../../../../components/MultipleAccountsSelection/types';
import useSignedInNavigation from '../../../../../hooks/useSignedInNavigation';
import { CustomerDetailStackParamList } from '../../../../../routes/types';
import { CustomerDetailFullAccount } from '../../../../../store/customerDetail/types';
import { useCustomerDetailContext } from '../../../context';

import { InfoPanelAccount } from './Account';
import { Info } from './Info';
import { InfoPanelProfile } from './Profile';
import { InfoPanelVisible } from './Visible';

export declare namespace InfoPanelTypes {
  interface Props {
    customer: CustomerDetailFullAccount | null | undefined;
    isLoading?: boolean;
    hasError?: boolean;
    onRefresh?: () => void;
    accountSelected?: MultipleAccountsSelectionProps['accountSelected'];
    handleChangeAccount?: MultipleAccountsSelectionProps['setAccountSelected'];
  }

  interface PanelProfileProps extends Props {
    profileNavigate: () => void;
  }

  interface PanelAccountProps extends Props {
    accountNavigate: () => void;
  }
}

export const InfoPanel: React.FC = memo(() => {
  const { isLoading, hasError, onRefresh, customer, handleChangeAccount, accountSelected } =
    useCustomerDetailContext();
  const { params } = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const [accordionState, setAccordionState] = useState<AccordionTypes.Status>('closed');

  const handlePress = () => {
    setAccordionState(accordionState === 'closed' ? 'opened' : 'closed');
  };

  const handleClosed = () => {
    setAccordionState('closed');
  };

  const { navigate: profileNavigate } = useSignedInNavigation(
    'CustomerDetailGroup',
    {
      ...params,
      selectedTab: 'cadastro',
      accountInitialTab: 'personalData',
    },
    {},
    'replace'
  );

  const { navigate: accountNavigate } = useSignedInNavigation(
    'CustomerDetailGroup',
    {
      ...params,
      selectedTab: 'cadastro',
      accountInitialTab: 'financialData',
    },
    {},
    'replace'
  );

  const accountNavigateHandler = useCallback(() => {
    accountNavigate();
    setAccordionState('closed');
  }, [accountNavigate]);

  const profileNavigateHandler = useCallback(() => {
    profileNavigate();
    setAccordionState('closed');
  }, [profileNavigate]);

  const expandedContent = useMemo(() => {
    const sharedContent = (
      <>
        <InfoPanelProfile
          isLoading={isLoading}
          customer={customer}
          profileNavigate={profileNavigateHandler}
          handleChangeAccount={handleChangeAccount}
          accountSelected={accountSelected}
        />
        <InfoPanelAccount
          isLoading={isLoading}
          customer={customer}
          accountNavigate={accountNavigateHandler}
          handleChangeAccount={handleChangeAccount}
          accountSelected={accountSelected}
        />
      </>
    );

    if (isMobile) {
      if (hasError) {
        return <ErrorBoundary onRefresh={onRefresh} />;
      }
      return (
        <>
          <VStack space="stack.xxxs">
            <Text variant="bold" fontSize={Typography['fontsize.lg']}>
              Detalhes do Cliente
            </Text>
            <Shadow level={1} borderRadius={BoxRadius.sm} padding="inset.xs">
              <Info
                isLoading={isLoading}
                customer={customer}
                accountSelected={accountSelected}
                handleChangeAccount={handleChangeAccount}
                otherFields
              />
            </Shadow>
            {sharedContent}
            <Button
              alignSelf="center"
              color="brand.tertiary.principal"
              variant="textLink"
              onPress={accountNavigateHandler}
              _text={{ fontFamily: Typography['fontfamily.base.semibold'] }}
              rightIcon={
                <Icon
                  name="ChevronRight"
                  size="xxxs"
                  testID="icon"
                  color="brand.tertiary.principal"
                />
              }>
              IR PARA CADASTRO
            </Button>
          </VStack>
        </>
      );
    }

    return <VStack space="stack.xxxs">{sharedContent}</VStack>;
  }, [
    customer,
    isLoading,
    hasError,
    onRefresh,
    isMobile,
    accountSelected,
    handleChangeAccount,
    accountNavigateHandler,
    profileNavigateHandler,
  ]);

  if (hasError && !isMobile) {
    return (
      <Shadow level={1} borderRadius={BoxRadius.sm}>
        <ErrorBoundary onRefresh={onRefresh} />
      </Shadow>
    );
  }

  return (
    <Accordion
      testId="customer-info-panel"
      status={accordionState}
      onPress={handlePress}
      onClosed={handleClosed}
      hasShadow={!isMobile}>
      <Accordion.Visible
        containerProps={
          isMobile
            ? { paddingBottom: 0, paddingLeft: 0, paddingRight: 0, paddingTop: 0 }
            : undefined
        }
        padding={isMobile ? 0 : 'inset.xs'}
        hideDefaultIcons={isMobile}>
        <InfoPanelVisible
          isLoading={isLoading}
          hasError={hasError}
          customer={customer}
          onToggleAccordion={handlePress}
          accountSelected={accountSelected}
          handleChangeAccount={handleChangeAccount}
        />
      </Accordion.Visible>
      <Accordion.Expanded>{expandedContent}</Accordion.Expanded>
    </Accordion>
  );
});
